<template>
  <div class="kf-table-wrap neumorphism-flat">
    <!-- header strip: title + count + right-hand actions -->
    <div v-if="title || hasActions" class="kf-table__head">
      <div class="kf-table__title">
        {{ title }}
        <span
          v-if="!error && totalCount !== null && (!loading || items.length)"
          class="kf-table__count"
        >
          {{ totalCount }}
        </span>
      </div>
      <div class="kf-table__actions">
        <slot name="actions"></slot>
      </div>
    </div>

    <ErrorState v-if="error" :message="error" @retry="$emit('retry')" />

    <!-- Skeletons are for a FIRST load, where there is nothing to preserve.
         Once the table has rows they stay mounted and the refresh shows as a
         thin bar instead (see :loading on v-data-table below).

         This used to fire on any load at all, which tore the whole table down
         mid-refresh: reordering a product blanked the rows you had just dragged,
         re-mounted every one of them and reset the horizontal scroll. The old
         Products screen never had that problem because it bound :loading
         straight to v-data-table, which leaves the tbody alone. -->
    <div v-else-if="loading && !items.length" class="pa-4">
      <v-skeleton-loader
        v-for="n in skeletonRows"
        :key="n"
        type="list-item-avatar-two-line"
        class="mb-1 kf-skeleton"
      />
    </div>

    <EmptyState
      v-else-if="!items.length"
      :title="emptyTitle"
      :message="emptyMessage"
      :icon="emptyIcon"
    >
      <template v-if="$slots.empty" #action><slot name="empty"></slot></template>
    </EmptyState>

    <template v-else>
      <!-- Wide tables scroll inside this container so the page body never
           scrolls horizontally. -->
      <div class="kf-table__scroll">
        <v-data-table
          :headers="headers"
          :items="items"
          :items-per-page="items.length"
          :loading="loading"
          hide-default-footer
          disable-sort
          class="kf-table"
          :item-key="itemKey"
          :show-expand="showExpand"
          :single-expand="singleExpand"
          :expanded="expanded"
          :item-class="rowClass"
          @update:expanded="$emit('update:expanded', $event)"
          @click:row="(item, slotData) => $emit('row-click', item, slotData)"
        >
          <!-- Forward only cell/column slots. In Vue 2.6 every v-slot lands in
               $scopedSlots, so forwarding everything would also hand this
               table's own "actions" / "empty" slots to v-data-table. -->
          <template v-for="name in tableSlots" v-slot:[name]="slotData">
            <slot :name="name" v-bind="slotData" />
          </template>
        </v-data-table>
      </div>

      <div v-if="totalPages > 1" class="kf-table__footer">
        <span class="kf-table__range">{{ rangeLabel }}</span>
        <v-pagination
          :value="page"
          :length="totalPages"
          :total-visible="7"
          color="primary"
          @input="$emit('page-change', $event)"
        />
      </div>
    </template>
  </div>
</template>

<script>
import EmptyState from "./EmptyState.vue";
import ErrorState from "./ErrorState.vue";

export default {
  name: "KfDataTable",
  components: { EmptyState, ErrorState },
  props: {
    title: { type: String, default: "" },
    headers: { type: Array, required: true },
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    error: { type: [String, Object, Error], default: null },
    page: { type: Number, default: 1 },
    totalPages: { type: Number, default: 1 },
    totalCount: { type: Number, default: null },
    pageLength: { type: Number, default: 10 },
    emptyTitle: { type: String, default: "No records match these filters" },
    emptyMessage: {
      type: String,
      default: "Try widening the date range or clearing the search.",
    },
    emptyIcon: { type: String, default: "inbox" },
    skeletonRows: { type: Number, default: 6 },

    /* -------------------------------------------------- row expansion ----
     * Off unless a page opts in. Vuetify keys rows by `itemKey`, so a table
     * with expansion needs one unique value per parent row.
     */
    itemKey: { type: String, default: "id" },
    showExpand: { type: Boolean, default: false },
    singleExpand: { type: Boolean, default: false },
    /** Use with .sync -- the open rows. */
    expanded: { type: Array, default: () => [] },
  },
  computed: {
    /**
     * A <template #actions> with no slot props lands on $slots in Vue 2.6, but
     * checking both is what keeps the header strip from silently not rendering
     * on the screens that pass no title.
     */
    hasActions() {
      return !!(this.$slots.actions || this.$scopedSlots.actions);
    },

    /** Slot names that belong to v-data-table rather than to this wrapper. */
    tableSlots() {
      return Object.keys(this.$scopedSlots || {}).filter(
        (name) =>
          name.indexOf("item.") === 0 ||
          name.indexOf("header.") === 0 ||
          name === "body" ||
          name === "expanded-item"
      );
    },
    /**
     * An open parent is tagged so its bottom rule can be dropped -- otherwise
     * the forced `border-bottom` on every cell draws a line between a row and
     * its own children, and an expanded row reads as closed.
     */
    rowClass() {
      const open = this.expanded;
      return (item) =>
        open.indexOf(item) === -1 ? "kf-row" : "kf-row kf-row--expanded";
    },
    rangeLabel() {
      if (this.totalCount === null) return "";
      const start = (this.page - 1) * this.pageLength + 1;
      const end = Math.min(this.page * this.pageLength, this.totalCount);
      return `${start}–${end} of ${this.totalCount}`;
    },
  },
};
</script>

<style scoped>
.kf-table-wrap {
  background: var(--kf-surface);
  border-radius: 10px;
  overflow: hidden;
}
.kf-table__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px 10px;
  flex-wrap: wrap;
}
.kf-table__title {
  font-size: 15px;
  font-weight: 700;
  color: var(--kf-brand);
  display: flex;
  align-items: center;
  gap: 8px;
}
.kf-table__count {
  background: var(--kf-brand-soft);
  color: var(--kf-brand);
  border-radius: 20px;
  padding: 1px 9px;
  font-size: var(--kf-fs-label);
  font-weight: 700;
}
.kf-table__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.kf-table__scroll {
  overflow-x: auto;
}
.kf-table__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 16px 12px;
  flex-wrap: wrap;
}
.kf-table__range {
  font-size: var(--kf-fs-meta);
  color: #000;
}
.kf-skeleton {
  background: transparent;
}
</style>

<style>
/* Unscoped: v-data-table renders its own markup, so these need to reach it.
   Namespaced under .kf-table so the legacy tables are unaffected. */
.kf-table.v-data-table {
  background: transparent;
}
.kf-table.v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  background: var(--kf-brand);
  color: #ffffff !important;
  /* Size and weight come from the global header rule in app-css/kf-tokens.css. */
  white-space: nowrap;
  height: var(--kf-head-h);
}
.kf-table.v-data-table > .v-data-table__wrapper > table > thead > tr > th:first-child {
  border-top-left-radius: 8px;
}
.kf-table.v-data-table > .v-data-table__wrapper > table > thead > tr > th:last-child {
  border-top-right-radius: 8px;
}
.kf-table.v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
  border-bottom: 1px solid var(--kf-border) !important;
  /* Geometry AND text now live in kf-tokens.css -- size, weight and colour were
     duplicated here and had drifted from the legacy skin. Only the divider is
     still this block's own. */
  height: var(--kf-row-h);
}
.kf-table.v-data-table > .v-data-table__wrapper > table > tbody > tr.kf-row:hover {
  background: var(--kf-hover) !important;
}

/* ------------------------------------------------------- expanded rows ---
   The rules above fix every cell at 48px with a bottom border. Both are wrong
   for a row that has children: the child block needs its own height, and a
   line between a parent and its children makes an open row look closed. */
.kf-table.v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr.kf-row--expanded
  > td {
  border-bottom: none !important;
}
.kf-table.v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr
  > td.kf-expanded {
  height: auto !important;
  padding: 0 !important;
}
</style>
